import { BigNumber } from '@ethersproject/bignumber'
import { formatUnits, parseUnits } from '@ethersproject/units'
import { Currency, CurrencyAmount } from '@uniswap/sdk-core'
import { Position } from '@uniswap/v3-sdk'
import { useMemo } from 'react'

import { useToken } from './Tokens'
import { useIncentiveConfig } from './useIncentive'
import { usePool } from './usePools'
import usePriceInWhydra from './usePriceInWhydra'

interface StakingPosition {
  liquidity: BigNumber
  tickLower: number
  tickUpper: number
}

export function usePositionAPR(positionDetails: StakingPosition | undefined, incentiveId: string | undefined) {
  const { liquidity, tickLower, tickUpper } = positionDetails ?? {}

  const incentiveConfig = useIncentiveConfig(incentiveId ?? '')

  const rewardToken = useToken(incentiveConfig?.key.rewardToken)
  const reward = useMemo(() => {
    if (rewardToken && incentiveConfig) {
      try {
        const durationDays = (Number(incentiveConfig.key.endTime) - Number(incentiveConfig.key.startTime)) / 86400
        const reward = formatUnits(incentiveConfig.reward, rewardToken.decimals)
        const dailyReward = Number(reward) / durationDays
        const annualReward = Math.floor(dailyReward * 365.25)

        return CurrencyAmount.fromRawAmount(
          rewardToken ?? undefined,
          parseUnits(annualReward.toString(), rewardToken?.decimals).toString()
        )
      } catch (error) {
        console.error(error)
      }
    }

    return null
  }, [rewardToken, incentiveConfig])
  const rewardTokenPrice = usePriceInWhydra(rewardToken ?? undefined)

  const whydraValueOfReward: CurrencyAmount<Currency> | null = useMemo(() => {
    if (!rewardTokenPrice || !reward) return null
    return rewardTokenPrice.quote(reward)
  }, [rewardTokenPrice, reward])

  const token0 = useToken(incentiveConfig?.tokenIdA)
  const token1 = useToken(incentiveConfig?.tokenIdB)

  const [, pool] = usePool(token0 ?? undefined, token1 ?? undefined, incentiveConfig?.fee)
  const position = useMemo(() => {
    if (pool && liquidity && typeof tickLower === 'number' && typeof tickUpper === 'number') {
      return new Position({
        pool,
        liquidity: liquidity.toString(),
        tickLower,
        tickUpper,
      })
    }
    return undefined
  }, [pool, liquidity, tickLower, tickUpper])

  // whydra prices
  const price0 = usePriceInWhydra(pool?.token0 ?? undefined)
  const price1 = usePriceInWhydra(pool?.token1 ?? undefined)

  const whydraValueOfLiquidity: CurrencyAmount<Currency> | null = useMemo(() => {
    if (!price0 || !price1 || !position) return null
    const amount0 = price0.quote(position.amount0)
    const amount1 = price1.quote(position.amount1)
    return amount0.add(amount1)
  }, [price0, price1, position])

  const apr = useMemo<string | null>(() => {
    if (positionDetails && pool && whydraValueOfReward && whydraValueOfLiquidity && incentiveConfig) {
      const { liquidity: positionLiquidity, tickLower, tickUpper } = positionDetails
      const { liquidity: poolLiquidity, tickCurrent } = pool

      // Check if position is out of range or campaign is inactive
      const timestamp = new Date().getTime() / 1000
      if (
        tickCurrent < tickLower ||
        tickCurrent > tickUpper ||
        timestamp < Number(incentiveConfig.key.startTime) ||
        timestamp > Number(incentiveConfig.key.endTime)
      ) {
        return '0.00'
      } else {
        // Position is active
        // trycatch as precaution against overflow
        try {
          const shareBN = BigNumber.from(poolLiquidity.toString()).div(positionLiquidity)
          const share = 1 / shareBN.toNumber()
          const earnProjection = share * Number(whydraValueOfReward.toSignificant(8))
          const apr = (earnProjection / Number(whydraValueOfLiquidity.toSignificant(8))) * 100
          return apr.toFixed(2)
        } catch (error) {
          console.error(error)
          return null
        }
      }
    }

    return null
  }, [positionDetails, pool, whydraValueOfReward, whydraValueOfLiquidity, incentiveConfig])

  return apr
}
